<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="$t(context?.isResend ? 'recruitment.submission.resendTitle' : 'recruitment.submission.sendTitle')"
    :subtitle="contextSubtitle"
    header-icon="mdi-email-arrow-right-outline"
    :loading="sending"
    :save-label="$t(context?.isResend ? 'recruitment.submission.resend' : 'recruitment.submission.send')"
    :save-disabled="sendDisabled"
    max-width="760"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="send"
  >
    <div class="submission-drawer pa-6">
      <v-tabs v-model="tab" color="primary" density="compact" class="mb-5">
        <v-tab value="compose">{{ $t("recruitment.submission.compose") }}</v-tab>
        <v-tab value="history">
          {{ $t("recruitment.submission.history") }}
          <v-chip size="x-small" class="ml-2">{{ history.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-5" />
      <v-alert v-if="errorMessage" type="error" density="compact" class="mb-5">
        {{ errorMessage }}
      </v-alert>
      <v-alert v-if="successMessage" type="success" density="compact" class="mb-5">
        {{ successMessage }}
      </v-alert>
      <v-alert v-if="readOnly" type="info" density="compact" class="mb-5">
        {{ $t("recruitment.submission.readOnly") }}
      </v-alert>

      <div v-if="tab === 'compose' && context" class="submission-drawer__compose">
        <v-alert v-if="context.senderError" type="error" density="compact" class="mb-5">
          {{ context.senderError }}
        </v-alert>
        <div v-else-if="context.senderPreview" class="submission-drawer__sender mb-5">
          <v-icon size="18" color="primary">mdi-account-arrow-right-outline</v-icon>
          <div>
            <div class="text-caption">{{ $t("recruitment.submission.sender") }}</div>
            <strong>{{ senderLabel }}</strong>
          </div>
        </div>

        <section class="submission-drawer__section">
          <div class="submission-drawer__section-head">
            <div>
              <div class="submission-drawer__section-title">{{ $t("recruitment.submission.recipients") }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t("recruitment.submission.separateEmails") }}
              </div>
            </div>
            <v-btn variant="text" size="small" @click="toggleAllRecipients">
              {{ $t(allRecipientsSelected ? "recruitment.submission.clearAll" : "recruitment.submission.selectAll") }}
            </v-btn>
          </div>
          <div class="submission-drawer__choice-list">
            <label v-for="contact in context.contacts" :key="contact.ncrm_person_id" class="submission-drawer__choice">
              <v-checkbox-btn
                v-model="selectedRecipientIds"
                :value="contact.ncrm_person_id"
                :disabled="recipientSelectionDisabled(contact.ncrm_person_id)"
              />
              <span class="submission-drawer__choice-content">
                <strong>{{ contact.full_name }}</strong>
                <small>{{ contact.email_primary }}</small>
              </span>
            </label>
          </div>
          <v-alert v-if="!context.contacts.length" type="warning" density="compact" class="mt-3">
            {{ $t("recruitment.submission.noRecipients") }}
          </v-alert>
        </section>

        <v-divider class="my-6" />
        <section class="submission-drawer__section">
          <div class="submission-drawer__section-title mb-3">{{ $t("recruitment.submission.documents") }}</div>
          <div class="submission-drawer__choice-list">
            <label v-for="document in context.documents" :key="document.id" class="submission-drawer__choice">
              <v-checkbox-btn
                v-model="selectedDocumentIds"
                :value="document.id"
                :disabled="documentSelectionDisabled(document)"
              />
              <v-icon size="18" :color="document.available ? 'primary' : 'error'">mdi-file-document-outline</v-icon>
              <span class="submission-drawer__choice-content">
                <strong>{{ document.filename }}</strong>
                <small>
                  {{ document.available ? formatBytes(document.size_bytes) : $t("recruitment.submission.unavailable") }}
                </small>
              </span>
            </label>
          </div>
          <div v-if="context.documents.length" class="text-caption text-medium-emphasis mt-2">
            {{ formatBytes(selectedAttachmentBytes) }} / {{ formatBytes(context.limits.attachmentBytes) }}
          </div>
          <v-alert v-if="attachmentsTooLarge" type="warning" density="compact" class="mt-3">
            {{ $t("recruitment.submission.attachmentsTooLarge") }}
          </v-alert>
          <v-alert v-if="!context.documents.length" type="warning" density="compact">
            {{ $t("recruitment.submission.noDocuments") }}
          </v-alert>
        </section>

        <v-divider class="my-6" />
        <section class="submission-drawer__section">
          <div class="submission-drawer__section-title mb-3">{{ $t("recruitment.submission.email") }}</div>
          <v-text-field
            v-model="subjectTemplate"
            :label="$t('recruitment.submission.subject')"
            maxlength="500"
          />
          <v-textarea
            v-model="bodyTemplate"
            :label="$t('recruitment.submission.message')"
            rows="10"
            auto-grow
            maxlength="100000"
          />

          <v-expansion-panels variant="accordion" class="mt-3">
            <v-expansion-panel :title="$t('recruitment.submission.preview')">
              <v-expansion-panel-text>
                <div class="submission-drawer__preview-subject">{{ previewSubject }}</div>
                <div class="submission-drawer__preview-body">{{ previewBody }}</div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </div>

      <div v-else-if="tab === 'history'" class="submission-drawer__history">
        <v-alert v-if="!loading && !history.length" type="info">
          {{ $t("recruitment.submission.noHistory") }}
        </v-alert>
        <v-expansion-panels v-else variant="accordion">
          <v-expansion-panel v-for="batch in history" :key="batch.id">
            <v-expansion-panel-title>
              <div class="submission-drawer__history-title">
                <div>
                  <strong>{{ formatDateTime(batch.created_at) }}</strong>
                  <div class="text-caption text-medium-emphasis">
                    {{ batch.sent_by_name || batch.sender_name_snapshot || "—" }} · {{ batch.documents?.length || 0 }}
                    {{ $t("recruitment.submission.documentCount") }}
                  </div>
                </div>
                <v-chip :color="batchColor(batch.status)">
                  {{ $t(`recruitment.submission.batchStatus.${batch.status}`) }}
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="submission-drawer__history-meta">
                <dt>{{ $t("recruitment.submission.sender") }}</dt>
                <dd>{{ batch.sender_name_snapshot || "—" }} &lt;{{ batch.sender_email_snapshot || "—" }}&gt;</dd>
                <dt>{{ $t("recruitment.submission.subject") }}</dt>
                <dd>{{ batch.subject_template }}</dd>
                <dt>{{ $t("recruitment.submission.documents") }}</dt>
                <dd>{{ (batch.documents || []).map((document) => document.filename_snapshot).join(", ") || "—" }}</dd>
              </dl>
              <div class="submission-drawer__deliveries mt-4">
                <div v-for="delivery in batch.deliveries || []" :key="delivery.id" class="submission-drawer__delivery">
                  <div>
                    <strong>{{ delivery.recipient_name_snapshot }}</strong>
                    <div class="text-caption text-medium-emphasis">{{ delivery.recipient_email_snapshot }}</div>
                    <div v-if="delivery.error_message" class="text-caption text-error mt-1">
                      {{ delivery.error_message }}
                    </div>
                    <div v-if="delivery.activity_log_status === 'failed'" class="text-caption text-warning mt-1">
                      {{ $t("recruitment.submission.activityWarning") }}
                    </div>
                  </div>
                  <v-chip size="x-small" :color="deliveryColor(delivery.status)">
                    {{ $t(`recruitment.submission.deliveryStatus.${delivery.status}`) }}
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  createSubmissionIdempotencyKey,
  renderRecruitmentTemplatePreview,
} from "@/Utile/recruitmentSubmissions.mjs";

export default {
  name: "RecruitmentSubmissionDrawer",
  components: { NcrmRecordEditDialog },
  props: {
    modelValue: { type: Boolean, default: false },
    applicationId: { type: [Number, String], default: null },
    readOnly: { type: Boolean, default: false },
    initialTab: { type: String, default: "compose" },
  },
  emits: ["update:modelValue", "sent"],
  data() {
    return {
      tab: "compose",
      loading: false,
      sending: false,
      errorMessage: "",
      successMessage: "",
      context: null,
      history: [],
      selectedRecipientIds: [],
      selectedDocumentIds: [],
      subjectTemplate: "",
      bodyTemplate: "",
      idempotencyKey: null,
    };
  },
  computed: {
    contextSubtitle() {
      const application = this.context?.application;
      return application ? `${application.candidateName} — ${application.position}` : "";
    },
    senderLabel() {
      const sender = this.context?.senderPreview?.sender || {};
      return sender.fromName ? `${sender.fromName} <${sender.fromEmail}>` : sender.fromEmail || "—";
    },
    allRecipientsSelected() {
      const all = this.context?.contacts || [];
      return all.length > 0 && all.every((contact) => this.selectedRecipientIds.includes(contact.ncrm_person_id));
    },
    previewVariables() {
      const recipient = (this.context?.contacts || []).find((contact) =>
        this.selectedRecipientIds.includes(contact.ncrm_person_id)
      ) || this.context?.contacts?.[0] || {};
      const application = this.context?.application || {};
      return {
        recipient_first_name: recipient.first_name || recipient.full_name || "",
        recipient_full_name: recipient.full_name || "",
        client_company: application.clientCompany || "",
        candidate_name: application.candidateName || "",
        position: application.position || "",
        sender_name: this.context?.senderPreview?.sender?.fromName
          || this.context?.senderPreview?.sender?.fromEmail
          || "",
      };
    },
    previewSubject() {
      return renderRecruitmentTemplatePreview(this.subjectTemplate, this.previewVariables);
    },
    previewBody() {
      return renderRecruitmentTemplatePreview(this.bodyTemplate, this.previewVariables);
    },
    selectedAttachmentBytes() {
      return (this.context?.documents || [])
        .filter((document) => this.selectedDocumentIds.includes(document.id))
        .reduce((total, document) => total + Number(document.size_bytes || 0), 0);
    },
    attachmentsTooLarge() {
      return this.selectedAttachmentBytes > Number(this.context?.limits?.attachmentBytes || 15 * 1024 * 1024);
    },
    sendDisabled() {
      return this.tab !== "compose"
        || this.readOnly
        || this.loading
        || this.sending
        || !this.context
        || Boolean(this.context.senderError)
        || !this.selectedRecipientIds.length
        || !this.selectedDocumentIds.length
        || this.attachmentsTooLarge
        || !String(this.subjectTemplate || "").trim()
        || !String(this.bodyTemplate || "").trim();
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
    applicationId() {
      if (this.modelValue) this.load();
    },
  },
  methods: {
    async load() {
      if (!this.applicationId) return;
      this.loading = true;
      this.errorMessage = "";
      this.successMessage = "";
      this.tab = this.initialTab === "history" ? "history" : "compose";
      try {
        const [contextResponse, historyResponse] = await Promise.all([
          axios.get(`/candidati/candidate_applications/${this.applicationId}/submission-context`),
          axios.get(`/candidati/candidate_applications/${this.applicationId}/submissions`),
        ]);
        this.context = contextResponse.data;
        this.history = historyResponse.data || [];
        this.selectedRecipientIds = [...(this.context.preselectedRecipientPersonIds || [])];
        this.selectedDocumentIds = [];
        this.subjectTemplate = this.context.template?.subjectTemplate || "";
        this.bodyTemplate = this.context.template?.bodyTemplate || "";
        this.idempotencyKey = createSubmissionIdempotencyKey();
      } catch (error) {
        this.context = null;
        this.history = [];
        this.errorMessage = error?.response?.data?.error || this.$t("recruitment.submission.loadError");
      } finally {
        this.loading = false;
      }
    },
    toggleAllRecipients() {
      this.selectedRecipientIds = this.allRecipientsSelected
        ? []
        : (this.context?.contacts || [])
          .slice(0, Number(this.context?.limits?.recipients || 25))
          .map((contact) => contact.ncrm_person_id);
    },
    recipientSelectionDisabled(personId) {
      const limit = Number(this.context?.limits?.recipients || 25);
      return !this.selectedRecipientIds.includes(personId) && this.selectedRecipientIds.length >= limit;
    },
    documentSelectionDisabled(document) {
      const limit = Number(this.context?.limits?.documents || 10);
      return !document.available
        || (!this.selectedDocumentIds.includes(document.id) && this.selectedDocumentIds.length >= limit);
    },
    formatBytes(value) {
      const bytes = Number(value || 0);
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(date.getTime())) return String(value);
      const two = (part) => String(part).padStart(2, "0");
      return `${two(date.getDate())}.${two(date.getMonth() + 1)}.${date.getFullYear()}, ${two(date.getHours())}:${two(date.getMinutes())}`;
    },
    batchColor(status) {
      if (status === "complete") return "success";
      if (status === "partial" || status === "unknown") return "warning";
      if (status === "failed") return "error";
      return "primary";
    },
    deliveryColor(status) {
      if (status === "sent") return "success";
      if (status === "failed") return "error";
      if (status === "unknown") return "warning";
      return "primary";
    },
    async send() {
      if (this.sendDisabled) return;
      this.sending = true;
      this.errorMessage = "";
      try {
        const response = await axios.post(
          `/candidati/candidate_applications/${this.applicationId}/submissions`,
          {
            recipient_person_ids: this.selectedRecipientIds,
            document_ids: this.selectedDocumentIds,
            subject_template: this.subjectTemplate,
            body_template: this.bodyTemplate,
          },
          { headers: { "X-Idempotency-Key": this.idempotencyKey } }
        );
        this.$emit("sent", response.data);
        await this.load();
        this.successMessage = this.$t("recruitment.submission.completed");
        this.tab = "history";
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("recruitment.submission.sendError");
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped src="./RecruitmentSubmissionDrawer.css"></style>
